import { UseMutationOptions, useMutation } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Dashboard } from 'types/dashboard';
import { DashboardConfigurationSchema } from 'types/dashboard_schema';
import { DashboardTemplateSummary } from 'types/dashboard_template';

import { useCreateDashboard } from 'dashboards/hooks/useCreateDashboard';
import { useReadDashboardTemplate } from 'dashboards/hooks/useDashboardTemplate';

type UseCreateDashboardFromTemplateParams = {
  template: DashboardTemplateSummary;
  name?: string;
};

export function useCreateDashboardFromTemplate(
  options: UseMutationOptions<
    Dashboard,
    APIError,
    UseCreateDashboardFromTemplateParams
  > = {}
) {
  const readDashboardTemplate = useReadDashboardTemplate();
  const { mutateAsync: createDashboard } = useCreateDashboard();

  return useMutation(async ({ template, name }) => {
    const dashboardTemplate = await readDashboardTemplate(template.id);
    const parsedConfiguration = DashboardConfigurationSchema.safeParse(
      dashboardTemplate.configuration
    );

    if (!parsedConfiguration.success) {
      throw {
        code: 'invalid-dashboard-template',
        status: 400,
        message: 'The selected dashboard template configuration is not valid.',
      } satisfies APIError;
    }

    return createDashboard({
      name: name ?? template.name,
      configuration: parsedConfiguration.data,
    });
  }, options);
}
